import { useState } from 'react';

import { useCreateAppointment } from '../../hooks/use-appointments';
import { useToastStore } from '../../stores/toast-store';

import { typeLabel } from './agenda-adapter';

import type { AppointmentWireItem } from '../../lib/api-client';
import type { JSX } from 'react';

type ApptType = AppointmentWireItem['type'];

const TYPES: ApptType[] = [
  'technical_visit',
  'in_person_meeting',
  'video_call',
  'phone_call',
  'delivery',
  'other',
];

/**
 * AppointmentCreateModal — cria appointment avulso (source='attendant').
 * Idioma de modal do atendente. `scheduledAt` de datetime-local → ISO.
 */
export function AppointmentCreateModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}): JSX.Element | null {
  const create = useCreateAppointment();
  const [title, setTitle] = useState('');
  const [type, setType] = useState<ApptType>('technical_visit');
  const [scheduledAt, setScheduledAt] = useState('');
  const [durationMinutes, setDurationMinutes] = useState('60');
  const [addressOrLocation, setAddressOrLocation] = useState('');
  const [notes, setNotes] = useState('');

  if (!open) return null;

  const canSubmit = title.trim().length > 0 && scheduledAt.length > 0 && !create.isPending;

  const handleSubmit = (): void => {
    const dur = Number(durationMinutes);
    create.mutate(
      {
        title: title.trim(),
        type,
        scheduledAt: new Date(scheduledAt).toISOString(),
        source: 'attendant',
        durationMinutes: Number.isFinite(dur) && dur >= 5 ? dur : 60,
        ...(addressOrLocation.trim().length > 0 && { addressOrLocation: addressOrLocation.trim() }),
        ...(notes.trim().length > 0 && { notes: notes.trim() }),
      },
      {
        onSuccess: () => {
          onClose();
          setTitle('');
          setType('technical_visit');
          setScheduledAt('');
          setDurationMinutes('60');
          setAddressOrLocation('');
          setNotes('');
          useToastStore.getState().push({
            variant: 'success',
            title: 'Agenda',
            description: 'Agendamento criado.',
            durationMs: 3500,
          });
        },
        onError: (err) => {
          useToastStore.getState().push({
            variant: 'error',
            title: 'Falha ao criar agendamento',
            description: err instanceof Error ? err.message : 'Erro desconhecido',
            durationMs: 6000,
          });
        },
      },
    );
  };

  return (
    <div
      role="dialog"
      aria-label="Novo agendamento"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div
        className="w-full max-w-lg rounded-lg p-6 shadow-xl"
        style={{ background: 'var(--bg-surface)' }}
      >
        <h2 className="mb-4 text-lg font-semibold">Novo agendamento</h2>

        <label className="mb-3 block">
          <span className="mb-1 block text-sm font-medium">Título</span>
          <input
            className="w-full rounded border px-2 py-1"
            style={{ background: 'var(--bg-surface-2)', borderColor: 'var(--border-default)' }}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            placeholder="Ex.: Visita técnica — instalação de fibra"
          />
        </label>

        <div className="mb-3 grid grid-cols-2 gap-3">
          <label className="block">
            <span className="mb-1 block text-sm font-medium">Tipo</span>
            <select
              className="w-full rounded border px-2 py-1"
              style={{ background: 'var(--bg-surface-2)', borderColor: 'var(--border-default)' }}
              value={type}
              onChange={(e) => setType(e.target.value as ApptType)}
            >
              {TYPES.map((t) => (
                <option key={t} value={t}>
                  {typeLabel(t)}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium">Duração (min)</span>
            <input
              type="number"
              min={5}
              max={1440}
              className="w-full rounded border px-2 py-1"
              style={{ background: 'var(--bg-surface-2)', borderColor: 'var(--border-default)' }}
              value={durationMinutes}
              onChange={(e) => setDurationMinutes(e.target.value)}
            />
          </label>
        </div>

        <label className="mb-3 block">
          <span className="mb-1 block text-sm font-medium">Data e hora</span>
          <input
            type="datetime-local"
            className="w-full rounded border px-2 py-1"
            style={{ background: 'var(--bg-surface-2)', borderColor: 'var(--border-default)' }}
            value={scheduledAt}
            onChange={(e) => setScheduledAt(e.target.value)}
          />
        </label>

        <label className="mb-3 block">
          <span className="mb-1 block text-sm font-medium">Endereço/local (opcional)</span>
          <input
            className="w-full rounded border px-2 py-1"
            style={{ background: 'var(--bg-surface-2)', borderColor: 'var(--border-default)' }}
            value={addressOrLocation}
            onChange={(e) => setAddressOrLocation(e.target.value)}
            maxLength={500}
          />
        </label>

        <label className="mb-3 block">
          <span className="mb-1 block text-sm font-medium">Notas (opcional)</span>
          <textarea
            className="w-full rounded border px-2 py-1"
            style={{ background: 'var(--bg-surface-2)', borderColor: 'var(--border-default)' }}
            rows={2}
            maxLength={2000}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </label>

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            className="rounded border px-4 py-2 text-sm"
            style={{ borderColor: 'var(--border-default)' }}
            onClick={onClose}
            disabled={create.isPending}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {create.isPending ? 'Criando…' : 'Criar agendamento'}
          </button>
        </div>
      </div>
    </div>
  );
}
